import { humanize } from '@/utils/format';
import type { CertificateStatus } from '@/types/certificate';
import type { DomainStatus } from '@/types/domain';
import type { NodeStatus } from '@/types/node';
import type { OriginHealthStatus } from '@/types/originGroup';
import type { ReloadStatus } from '@/types/nginx';
import type { SubtaskStatus, TaskStatus, TaskType } from '@/types/task';
import type { UserRole } from '@/types/user';

/**
 * How a status value is displayed
 */
export interface StatusConfig {
  label: string;
  className: string;
}

/**
 * Typed status lookup with a safe fallback.
 *
 * Backend values the UI does not know yet (or null) never crash a page: they
 * render with a humanized label and a neutral class.
 */
export interface StatusMap<K extends string> {
  /** Known values, in display order (used for filter options) */
  readonly entries: Readonly<Record<K, StatusConfig>>;
  /** Known values as select options */
  readonly options: ReadonlyArray<{ value: K; label: string }>;
  /** Config for any value, falling back for unknown ones */
  get(status: string | null | undefined): StatusConfig;
  /** Whether the value is a known key */
  isKnown(status: string | null | undefined): status is K;
}

/**
 * Neutral class used for unknown values (styled globally in index.css)
 */
export const UNKNOWN_STATUS_CLASS = 'status-unknown';

export function createStatusMap<K extends string>(
  entries: Record<K, StatusConfig>,
  fallbackClassName: string = UNKNOWN_STATUS_CLASS
): StatusMap<K> {
  const isKnown = (status: string | null | undefined): status is K =>
    typeof status === 'string' && Object.prototype.hasOwnProperty.call(entries, status);

  return {
    entries,
    options: (Object.keys(entries) as K[]).map((value) => ({ value, label: entries[value].label })),
    isKnown,
    get(status) {
      if (isKnown(status)) return entries[status];
      return { label: humanize(status) || 'Unknown', className: fallbackClassName };
    },
  };
}

export const certificateStatusMap = createStatusMap<CertificateStatus>({
  active: { label: 'Active', className: 'status-active' },
  pending: { label: 'Pending', className: 'status-pending' },
  expired: { label: 'Expired', className: 'status-expired' },
  error: { label: 'Error', className: 'status-error' },
});

export const domainStatusMap = createStatusMap<DomainStatus>({
  active: { label: 'Active', className: 'status-active' },
  pending: { label: 'Pending', className: 'status-pending' },
  error: { label: 'Error', className: 'status-error' },
});

export const nodeStatusMap = createStatusMap<NodeStatus>({
  online: { label: 'Online', className: 'status-online' },
  offline: { label: 'Offline', className: 'status-offline' },
  degraded: { label: 'Degraded', className: 'status-degraded' },
});

export const originHealthMap = createStatusMap<OriginHealthStatus>(
  {
    healthy: { label: 'Healthy', className: 'health-healthy' },
    unhealthy: { label: 'Unhealthy', className: 'health-unhealthy' },
    unknown: { label: 'Unknown', className: 'health-unknown' },
  },
  'health-unknown'
);

export const reloadStatusMap = createStatusMap<ReloadStatus>(
  {
    success: { label: 'Success', className: 'nginx-status-success' },
    failed: { label: 'Failed', className: 'nginx-status-failed' },
  },
  'nginx-status-unknown'
);

export const taskStatusMap = createStatusMap<TaskStatus>({
  pending: { label: 'Pending', className: 'tasks-status-pending' },
  running: { label: 'Running', className: 'tasks-status-running' },
  completed: { label: 'Completed', className: 'tasks-status-completed' },
  failed: { label: 'Failed', className: 'tasks-status-failed' },
  cancelled: { label: 'Cancelled', className: 'tasks-status-cancelled' },
  partially_completed: { label: 'Partial', className: 'tasks-status-partially_completed' },
});

export const subtaskStatusMap = createStatusMap<SubtaskStatus>({
  pending: { label: 'pending', className: 'tasks-subtask-status-pending' },
  running: { label: 'running', className: 'tasks-subtask-status-running' },
  completed: { label: 'completed', className: 'tasks-subtask-status-completed' },
  failed: { label: 'failed', className: 'tasks-subtask-status-failed' },
  skipped: { label: 'skipped', className: 'tasks-subtask-status-skipped' },
});

export const taskTypeMap = createStatusMap<TaskType>(
  {
    certificate_renewal: { label: 'Certificate Renewal', className: 'tasks-type-certificate_renewal' },
    dns_sync: { label: 'DNS Sync', className: 'tasks-type-dns_sync' },
  },
  ''
);

export const userRoleMap = createStatusMap<UserRole>({
  admin: { label: 'Admin', className: 'users-role-admin' },
  operator: { label: 'Operator', className: 'users-role-operator' },
  readonly: { label: 'Read-only', className: 'users-role-readonly' },
});
